import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Formik } from "formik";
import styled from "styled-components";
import * as Yup from "yup";
import ReactDOMServer from "react-dom/server";
import {
  AppCard,
  AppCheckbox,
  AppDatePicker,
  AppFileUploader,
  AppMultiSelect,
  AppNoInfo,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from "@/layout/AppComponents";
import Moment from "moment";
import { toFormData } from "../../../minions/forms";
import Translator from "../../../layout/components/helpers/Translator";
import { axiosPath, path } from "../../../routing/helpers/router";
import BreadcrumbHandler from "../../../layout/components/breadcrumb/BreadcrumbHandler";
import CorrectiveActionsSidebar from "./CorrectiveActionsSidebar";
import * as currentPageAction from "../../../store/actions/currentPageAction";
import errorHandler from "@/layout/components/error/ErrorHandler";
import colors from "../../../constants/colors";
import SpinnerLoader from "../../../layout/components/helpers/SpinnerLoader";
import minions from "../../../minions/minions";
import AppTextEditor from "../../../layout/components/form/Texts/AppTextEditor";
import useIsMounted from "../../../layout/components/hooks/useIsMounted";
import * as moduleAction from "../../../store/actions/moduleAction";

export default function CorrectiveActionsTaskEditScreen() {
  const { moduleBridgeHex, taskID, filtersHex } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const isSidebarOpened = useSelector(
    (state) => state.auth?.settings?.preferences?.sidebar?.isOpened,
  );
  const permissions = useSelector(
    (s) => s.module[moduleBridgeHex]?.permissions?.correctiveActions,
  );
  const userBridge = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.userBridge,
  );
  const moduleData = useSelector((s) => s.module[moduleBridgeHex]?.moduleData);
  const [hasDependencyTaskCreation] = useState(
    userBridge?.permissions?.level === 0 ||
      userBridge?.permissions?.dependencyTaskCreation === true,
  );
  const [task, setTask] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [processOptions] = useState([
    {
      value: 0,
      label: "0%",
    },
    {
      value: 25,
      label: "25%",
    },
    {
      value: 50,
      label: "50%",
    },
    {
      value: 75,
      label: "75%",
    },
    {
      value: 100,
      label: "100%",
    },
  ]);

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // < LOAD TASK
  const loadTask = useCallback(
    async () =>
      axios
        .post(axiosPath("api_r_user_module_correctiveActions_task_get"), {
          moduleBridgeHex,
          taskID,
        })
        .then((r) => {
          if (isMounted.current) {
            setTask(r.data);
            setIsLoading(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, taskID],
  );

  useEffect(() => {
    loadTask();
  }, []);
  // > LOAD TASK

  // < EDIT
  const handleEdit = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_correctiveActions_task_edit"),
          toFormData({
            ...payload,
            ...{
              moduleBridgeHex,
              taskID,
            },
          }),
          { headers: { "Content-Type": "multipart/form-data" } },
        )
        .then(() => {
          if (isMounted.current) {
            navigate(
              path({
                name: "user_module_correctiveActions_task_list",
                params: {
                  moduleBridgeHex,
                  page: 1,
                  filtersHex,
                },
              }),
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, filtersHex],
  );
  // > EDIT

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    dispatch(
      moduleAction.fetch(
        axiosPath("api_r_user_module_correctiveActions_getModuleData"),
        "moduleData",
        {
          moduleBridgeHex,
        },
      ),
    );
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

    // < STORE CALLS
    const loadModulePermissions = useCallback(async () => {
      try {
        await dispatch(
          moduleAction.fetch(
            axiosPath("api_r_user_module_correctiveActions_getPermissions"),
            "permissions",
            {
              moduleBridgeHex,
              subType: "permissions",
            }
          )
        );
        await dispatch(
          moduleAction.fetch(
            axiosPath("api_r_user_module_correctiveActions_getPermissions"),
            "jsonSettings",
            {
              moduleBridgeHex,
              subType: "jsonSettings",
            }
          )
        );
        await dispatch(
          moduleAction.fetch(
            axiosPath("api_r_user_module_correctiveActions_getPermissions"),
            "userBridge",
            {
              moduleBridgeHex,
              subType: "userBridge",
            }
          )
        );
      } catch (err) {}
    }, [moduleBridgeHex]);
  
    useEffect(() => {
      loadModulePermissions();
    }, [moduleBridgeHex]);
    // < STORE CALLS

  const validationSchema = Yup.object().shape({
    dateStamp: Yup.string().required(
      ReactDOMServer.renderToString(
        <Translator file="module-legalCompliance" name="must.select.date" />,
      ),
    ),
    limitDate: Yup.string().required(
      ReactDOMServer.renderToString(
        <Translator file="module-legalCompliance" name="must.select.date" />,
      ),
    ),
    process: Yup.number()
      .nullable()
      .required(
        ReactDOMServer.renderToString(
          <Translator file="user" name="user.required" />,
        ),
      ),
    type: Yup.number()
      .nullable()
      .required(
        ReactDOMServer.renderToString(
          <Translator file="user" name="user.required" />,
        ),
      ),
    description: Yup.string().required(
      <Translator file="user" name="user.required" />,
    ),
    issuer: Yup.number()
      .nullable()
      .required(
        ReactDOMServer.renderToString(
          <Translator file="user" name="user.required" />,
        ),
      ),
  });

  if (isLoading) {
    return (
      <AppNoInfo color={colors.primaryLight}>
        <SpinnerLoader size={30} />
      </AppNoInfo>
    );
  }

  if (!moduleData) return <AppNoInfo />;

  if (
    userBridge?.permissions?.level === 0 ||
    userBridge?.permissions?.ownTaskEdition
  ) {
    return (
      <>
        <CorrectiveActionsSidebar />
        <BreadcrumbHandler
          paths={
            moduleData
              ? [
                  {
                    name: moduleData?.moduleName,
                    route: {
                      name: "user_module_correctiveActions_task_list",
                      params: {
                        moduleBridgeHex,
                        view: "my-tasks",
                        page: 1,
                        filtersHex: 0,
                      },
                    },
                  },
                  {
                    name: ReactDOMServer.renderToString(
                      <Translator
                        file="module-correctiveActions"
                        name="module.ca.edit-task-code"
                        variables={{ code: task?.code }}
                      />,
                    ),
                  },
                ]
              : [{ name: "..." }]
          }
        />
        <main role="main" className="col-md-12 col-lg-12 px-md-4">
          <div className={`${isSidebarOpened && "sidebar-expanded"}`}>
            <section className="content mb-5">
              <Formik
                validationSchema={validationSchema}
                validateOnChange={false}
                validateOnBlur={false}
                initialValues={{
                  dateStamp: task?.dateStamp || "",
                  limitDate: task?.limitStamp || "",
                  parent: task?.parent,
                  process: task?.process,
                  type: task?.type ?? null,
                  source: task?.source ?? null,
                  systems: task?.systems ?? [],
                  plants: task?.plants ?? [],
                  files: task?.files ?? [],
                  deletedFiles: [],
                  issuer: task?.issuer ?? null,
                  responsibles: task?.responsibles ?? [],
                  closedStamp: task?.closedStamp ?? "",
                  description: task?.description ?? "",
                  notificateCreator: task?.notificateCreator,
                  notificateEmployees: task?.notificateEmployees,
                }}
                onSubmit={handleEdit}
              >
                {({
                  setFieldValue,
                  handleSubmit,
                  values,
                  isSubmitting,
                  errors,
                }) => (
                  <form onSubmit={handleSubmit}>
                    <AppCard
                      header={ReactDOMServer.renderToString(
                        <Translator
                          file="module-correctiveActions"
                          name="module.ca.edit-task-code"
                          variables={{ code: task?.code }}
                        />,
                      )}
                    >
                      <div className="container-fluid">
                        <div className="row">
                          <div
                            className={`col-lg-${
                              hasDependencyTaskCreation ? 3 : 4
                            }`}
                          >
                            <AppDatePicker
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-correctiveActions"
                                  name="module.ca.dateStamp"
                                />,
                              )}
                              value={values.dateStamp}
                              onChange={(opt) =>
                                setFieldValue("dateStamp", opt)
                              }
                              placeholder="dd/mm/yyyy"
                              error={errors.dateStamp}
                              showMonthDropdown
                              showYearDropdown
                              required
                            />
                          </div>
                          <div
                            className={`col-lg-${
                              hasDependencyTaskCreation ? 3 : 4
                            }`}
                          >
                            <AppDatePicker
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-correctiveActions"
                                  name="module.ca.new-limitStamp"
                                />,
                              )}
                              value={values.limitDate}
                              onChange={(opt) =>
                                setFieldValue("limitDate", opt)
                              }
                              placeholder="dd/mm/yyyy"
                              error={errors.limitDate}
                              showMonthDropdown
                              showYearDropdown
                              required
                            />
                          </div>
                          <div
                            className={`col-lg-${
                              hasDependencyTaskCreation ? 3 : 4
                            }`}
                          >
                            <AppSingleSelect
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-correctiveActions"
                                  name="module.ca.new-advanced-percent"
                                />,
                              )}
                              icon={<AppRemixIcon icon="battery-low" />}
                              value={values.process}
                              options={processOptions}
                              onChange={(v) => setFieldValue("process", v)}
                              error={errors.process}
                              hasSearchBox
                            />
                          </div>
                          {hasDependencyTaskCreation && (
                            <div className="col-lg-3">
                              <AppSingleSelect
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-correctiveActions"
                                    name="module.ca.new-advanced-dependency"
                                  />,
                                )}
                                icon={<AppRemixIcon icon="git-commit" />}
                                value={values.parent}
                                options={moduleData?.tasks}
                                onChange={(v) => setFieldValue("parent", v)}
                                error={errors.parent}
                                isClearable
                                hasSearchBox
                              />
                            </div>
                          )}
                        </div>
                        <div className="row">
                          <div className="col-lg-6">
                            <AppSingleSelect
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-correctiveActions"
                                  name="module.ca.task-type"
                                />,
                              )}
                              icon={<AppRemixIcon icon="focus" />}
                              value={values.type}
                              options={moduleData?.types}
                              onChange={(v) => setFieldValue("type", v)}
                              error={errors.type}
                              hasSearchBox
                              required
                            />
                          </div>
                          <div className="col-lg-6">
                            <AppSingleSelect
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-correctiveActions"
                                  name="module.ca.maker"
                                />,
                              )}
                              icon={<AppRemixIcon icon="user" />}
                              value={values.issuer}
                              options={moduleData?.taskMakers}
                              onChange={(v) => setFieldValue("issuer", v)}
                              error={errors.issuer}
                              hasSearchBox
                              required
                            />
                          </div>
                        </div>
                        <div className="row">
                          {permissions?.source && (
                            <div className="col-lg-3">
                              <AppSingleSelect
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-correctiveActions"
                                    name="module.ca.source"
                                  />,
                                )}
                                icon={<AppRemixIcon icon="focus" />}
                                value={values.source}
                                options={moduleData?.sources}
                                onChange={(v) => setFieldValue("source", v)}
                                error={errors.source}
                                hasSearchBox
                              />
                            </div>
                          )}
                          {permissions?.systemCenters && (
                            <>
                              <div className="col-lg-3">
                                <AppMultiSelect
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-correctiveActions"
                                      name="module.ca.systems"
                                    />,
                                  )}
                                  icon={<AppRemixIcon icon="focus" />}
                                  value={values.systems}
                                  options={moduleData?.systems}
                                  onChange={(v) => setFieldValue("systems", v)}
                                  error={errors.systems}
                                  hasSearchBox
                                />
                              </div>
                              <div className="col-lg-3">
                                <AppMultiSelect
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="user"
                                      name="user.plants"
                                    />,
                                  )}
                                  icon={<AppRemixIcon icon="focus" />}
                                  value={values.plants}
                                  options={moduleData?.plants}
                                  isLoading={!moduleData?.plants}
                                  onChange={(v) => setFieldValue("plants", v)}
                                  error={errors.plants}
                                  hasSearchBox
                                  hasSelectOptions
                                />
                              </div>
                            </>
                          )}
                          {permissions?.responsibles && (
                            <div className="col-lg-3">
                              <AppMultiSelect
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-correctiveActions"
                                    name="module.ca.new.responsible"
                                  />,
                                )}
                                icon={<AppRemixIcon icon="group" />}
                                value={values.responsibles}
                                options={moduleData?.responsiblesAndIssuers}
                                isLoading={!moduleData?.responsiblesAndIssuers}
                                onChange={(v) =>
                                  setFieldValue("responsibles", v)
                                }
                                error={errors.responsibles}
                                hasSearchBox
                                hasSelectOptions
                              />
                            </div>
                          )}
                        </div>
                        {task?.closedStamp && (
                          <div className="row">
                            <div className="col-lg-6">
                              <AppDatePicker
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-correctiveActions"
                                    name="module.ca.edit-closedStamp"
                                  />,
                                )}
                                value={values?.closedStamp}
                                onChange={(opt) =>
                                  setFieldValue("closedStamp", opt)
                                }
                                placeholder="dd/mm/yyyy"
                                dateFormat="dd/MM/yyyy"
                                error={errors?.closedStamp}
                                maxDate={Moment().toDate()}
                                showMonthDropdown
                                showYearDropdown
                                required
                              />
                            </div>
                          </div>
                        )}
                        <div className="row">
                          <div className="col-lg-12">
                            <AppTextArea
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-correctiveActions"
                                  name="module.ca.description"
                                />,
                              )}
                              height="195px"
                              isReadOnly
                              value={
                                <AppTextEditor
                                  value={values.description}
                                  onChange={(opt) =>
                                    setFieldValue("description", opt)
                                  }
                                  toolbar={{
                                    toolbar: [["bold", "italic"]],
                                  }}
                                  height={138}
                                />
                              }
                              error={errors.description}
                              required
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-6">
                            <AppFileUploader
                              placeholder={
                                values.files.length === 0 ? (
                                  <Translator
                                    file="general"
                                    name="select.files"
                                  />
                                ) : (
                                  <Translator
                                    file="admin"
                                    name="general.elements-selected-elements"
                                    variables={{
                                      elements: values.files.length,
                                    }}
                                  />
                                )
                              }
                              onChange={(opt) => setFieldValue("file", opt)}
                              btnText={
                                <Translator file="general" name="search..." />
                              }
                              isMulti
                            />
                          </div>
                          <div className="col-lg-6">
                            <EvidenceFileList>
                              {values.files.map(
                                (f) =>
                                  !values.deletedFiles.includes(f.id) && (
                                    <li
                                      key={f.id}
                                      title={ReactDOMServer.renderToString(
                                        <Translator
                                          file="module-legalCompliance"
                                          name="click.delete"
                                        />,
                                      )}
                                      onClick={() =>
                                        setFieldValue("deletedFiles", [
                                          ...values.deletedFiles,
                                          f.id,
                                        ])
                                      }
                                    >
                                      <AppRemixIcon
                                        className="pointer mr-3"
                                        icon="delete-bin"
                                        color={colors.danger}
                                      />
                                      {minions.files.fileIcon(
                                        f.name,
                                        true,
                                        null,
                                        "mr-2",
                                      )}
                                      {f.name}
                                    </li>
                                  ),
                              )}
                            </EvidenceFileList>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-6">
                            <AppCheckbox
                              value={values.notificateCreator}
                              onChange={(v) =>
                                setFieldValue("notificateCreator", v)
                              }
                              className="m-0"
                            >
                              <Translator
                                file="module-correctiveActions"
                                name="new-email-notification-maker"
                              />
                            </AppCheckbox>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-6">
                            <AppCheckbox
                              value={values.notificateEmployees}
                              onChange={(v) =>
                                setFieldValue("notificateEmployees", v)
                              }
                              className="m-0"
                            >
                              <Translator
                                file="module-correctiveActions"
                                name="new-email-notification-employees"
                              />
                            </AppCheckbox>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-12">
                            <div className="float-right">
                              <button
                                type="submit"
                                className="btn btn-success btn-sm"
                                disabled={isSubmitting}
                              >
                                <Translator
                                  file="general"
                                  name="general.edit"
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </AppCard>
                  </form>
                )}
              </Formik>
            </section>
          </div>
        </main>
      </>
    );
  }
  return null;
}

const EvidenceFileList = styled.ul`
  list-style: none;
  margin: 10px 0 0 0;
  padding-left: 6px;
  background-color: ${colors.grey200};
  li {
    cursor: pointer;
    display: flex;
    align-items: center;
    border-top: 1px solid ${colors.grey300};
    padding: 8px 0;
    span {
      font-size: 12px;
      color: ${colors.primary};
    }
  }
  li:first-child {
    border-top: 0;
  }
`;
